import { useMemo } from 'react';

import { Box, type BoxProps, Text } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type {
  CascaderOption,
  CascaderOptionWithPath,
  CascaderCommonProps,
} from '../types';

export interface SingleSelectedValueProps<OptionType extends CascaderOption>
  extends BoxProps {
  selectedOption: CascaderOptionWithPath<OptionType>;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

interface InternalSingleSelectedValueProps<OptionType extends CascaderOption> {
  selectedOption: CascaderOptionWithPath<OptionType>;
}

export const InternalSingleSelectedValue = <OptionType extends CascaderOption>({
  selectedOption,
}: InternalSingleSelectedValueProps<OptionType>) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSingleSelectedValue = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SINGLE_SELECTED_VALUE),
    [getComponent],
  );

  return (
    <CustomSingleSelectedValue
      getCommonProps={getCommonProps}
      __css={cascaderStyles.singleSelectedValue}
      selectedOption={selectedOption}
      className="cascader-single-selected-value"
    />
  );
};

export const SingleSelectedValue = <OptionType extends CascaderOption>({
  getCommonProps: _,
  selectedOption,
  ...rest
}: SingleSelectedValueProps<OptionType>) => {
  return (
    <Box {...rest}>
      <Text size="md" as="span">
        {selectedOption.fullLabels.join(' / ')}
      </Text>
    </Box>
  );
};
